import type { PostgrestError } from '@supabase/supabase-js'
import { AppError } from '../errors'
import { supabase } from '../supabase/client'
import { historicoSchema, requisicaoItemSchema, requisicaoSchema } from '../../validation/schemas'
import { buyerChangesToJson } from '../../features/compras/mappers/buyer.mapper'
import { draftItemsToJson } from '../../features/requisicoes/mappers/requisicao.mapper'
import type { RequisicaoDetail } from '../../features/requisicoes/types'
import type { OutboxOperation } from './types'
import { getCachedProfile } from './cache'
import type { AuthenticatedUser } from '../../types/domain'
import type { Json } from '../supabase/database.types'

export function mapRemoteError(error: PostgrestError | null, fallback: string): AppError {
  if (!error) return new AppError('UNEXPECTED', fallback)
  if (error.code === 'PGRST116' || error.code === 'P0002') return new AppError('NOT_FOUND', 'Este registro não está mais disponível.', error)
  if (/^PGRST30/.test(error.code) || /jwt|session|token/i.test(error.message)) {
    return new AppError('AUTH', 'Sua sessão expirou. Entre novamente para continuar.', error)
  }
  if (error.code === '40001' || (error.code === 'P0001' && /CONFLITO_VERSAO/i.test(error.message))) {
    return new AppError('CONFLICT', 'Esta requisição foi atualizada em outra sessão. Revise os dados mais recentes antes de salvar novamente.', error)
  }
  if (error.code === '42501') return new AppError('PERMISSION', 'Você não possui permissão para esta alteração.', error)
  if (/fetch|network|timeout|abort|failed to fetch/i.test(error.message)) return new AppError('CONNECTION', 'Serviço temporariamente indisponível.', error)
  if (/23\d{3}|22\d{3}/.test(error.code)) return new AppError('VALIDATION', 'Os dados informados foram recusados pelo banco.', error)
  return new AppError('UNEXPECTED', fallback, error)
}

export async function currentSessionUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getSession()
  if (error || !data.session?.user.id) throw new AppError('AUTH', 'Sua sessão expirou. Entre novamente para continuar.', error)
  return data.session.user.id
}

export async function currentAuthenticatedUser(): Promise<AuthenticatedUser> {
  const userId = await currentSessionUserId()
  const cached = await getCachedProfile(userId)
  if (!cached) throw new AppError('AUTH', 'Sua sessão segura precisa ser validada online antes do uso offline.')
  return cached
}

export async function fetchRemoteDetail(id: string): Promise<RequisicaoDetail> {
  const [request, items, history] = await Promise.all([
    supabase.from('requisicoes').select('*').eq('id', id).single(),
    supabase.from('requisicao_itens').select('*').eq('requisicao_id', id).order('created_at'),
    supabase.from('historico_requisicoes').select('*').eq('requisicao_id', id).order('created_at', { ascending: false }),
  ])
  if (request.error) throw mapRemoteError(request.error, 'Requisição não encontrada.')
  if (items.error) throw mapRemoteError(items.error, 'Não foi possível carregar os itens.')
  if (history.error) throw mapRemoteError(history.error, 'Não foi possível carregar o histórico.')
  return {
    requisicao: requisicaoSchema.parse(request.data),
    itens: items.data.map((item) => requisicaoItemSchema.parse(item)),
    historico: history.data.map((entry) => historicoSchema.parse(entry)),
  }
}

export async function executeRemoteOperation(operation: OutboxOperation): Promise<RequisicaoDetail | null> {
  const payload = operation.payload
  if (payload.type === 'CREATE_ITEM_REQUEST') {
    const request = payload.request
    const result = await supabase.rpc('create_item_registration_request', {
      p_operation_id: operation.operationId, p_tipo: request.tipo, p_codigo: request.codigo,
      p_descricao: request.descricao, p_grade_1: request.grade_1, p_grade_2: request.grade_2,
      p_grade_3: request.grade_3, p_unidade: request.unidade, p_categoria: request.categoria,
      p_motivo: request.motivo, p_requisicao_id: request.requisicaoId ?? undefined, p_requisicao_item_id: request.requisicaoItemId ?? undefined,
    })
    if (result.error) throw mapRemoteError(result.error, 'Não foi possível enviar a solicitação de cadastro.')
    return null
  }
  if (payload.type === 'CREATE_REQUISICAO') {
    const result = await supabase.rpc('create_requester_requisition_offline', {
      p_operation_id: operation.operationId,
      p_requisicao_id: payload.localRequisicaoId,
      p_data_requisicao: payload.draft.data_requisicao,
      p_obra: payload.draft.obra,
      p_setor: payload.draft.setor,
      p_justificativa: payload.draft.justificativa,
      p_solicitante_nome: payload.draft.solicitante_nome,
      p_responsavel_nome: payload.draft.responsavel_nome,
      p_itens: draftItemsToJson(payload.draft.itens),
    })
    if (result.error) throw mapRemoteError(result.error, 'Não foi possível criar a requisição.')
    return fetchRemoteDetail(payload.localRequisicaoId)
  }

  if (payload.type === 'SAVE_REQUISICAO') {
    const draft = payload.draft
    if (!draft.id || draft.versao === null) throw new AppError('VALIDATION', 'A requisição não possui uma versão válida.')
    const result = await supabase.rpc('save_requester_requisition_offline', {
      p_operation_id: operation.operationId,
      p_requisicao_id: draft.id,
      p_expected_version: draft.versao,
      p_data_requisicao: draft.data_requisicao,
      p_obra: draft.obra,
      p_setor: draft.setor,
      p_justificativa: draft.justificativa,
      p_solicitante_nome: draft.solicitante_nome,
      p_responsavel_nome: draft.responsavel_nome,
      p_itens: draftItemsToJson(draft.itens),
      p_removed_itens: draft.removedItens.map((item): Json => ({ id: item.id, versao: item.versao })),
    })
    if (result.error) throw mapRemoteError(result.error, 'Não foi possível salvar a requisição.')
    return fetchRemoteDetail(draft.id)
  }

  if (payload.type === 'SAVE_COMPRA') {
    const result = await supabase.rpc('save_buyer_changes_offline', {
      p_operation_id: operation.operationId,
      p_requisicao_id: payload.requisicaoId,
      p_changes: buyerChangesToJson(payload.changes),
    })
    if (result.error) throw mapRemoteError(result.error, 'Não foi possível salvar os dados da compra.')
    return fetchRemoteDetail(payload.requisicaoId)
  }

  if (payload.type === 'BLOCK_REQUISICAO') {
    const result = await supabase.rpc('block_requisition_offline', {
      p_operation_id: operation.operationId,
      p_requisicao_id: payload.requisicaoId,
      p_expected_version: payload.expectedVersion,
    })
    if (result.error) throw mapRemoteError(result.error, 'Não foi possível bloquear a requisição.')
    return fetchRemoteDetail(payload.requisicaoId)
  }

  const result = await supabase.rpc('soft_delete_requisition_offline', {
    p_operation_id: operation.operationId,
    p_requisicao_id: payload.requisicaoId,
    p_expected_version: payload.expectedVersion,
  })
  if (result.error) throw mapRemoteError(result.error, 'Não foi possível excluir a requisição.')
  return null
}
